import { Node, mergeAttributes } from '@tiptap/core';
import { VueNodeViewRenderer } from '@tiptap/vue-3';
import MusicSheetNodeView from '@/components/common/MusicSheetNodeView.vue';

export const MusicSheetExtension = Node.create({
  name: 'musicSheet',
  group: 'block',
  atom: true,
  selectable: true,
  // Enables native ProseMirror node drag & drop. The actual drag is started
  // from the [data-drag-handle] element in MusicSheetNodeView.vue — without
  // a dedicated handle the browser never initiates the node drag because the
  // inner content is interactive (buttons, SVG score).
  draggable: true,

  addAttributes() {
    return {
      title: {
        default: 'Partitura',
        parseHTML: element => element.getAttribute('data-title') || 'Partitura',
        renderHTML: attributes => ({ 'data-title': attributes.title }),
      },
      timeSignature: {
        default: '4/4',
        parseHTML: element => element.getAttribute('data-time-signature') || '4/4',
        renderHTML: attributes => ({ 'data-time-signature': attributes.timeSignature }),
      },
      key: {
        default: 'C',
        parseHTML: element => element.getAttribute('data-key') || 'C',
        renderHTML: attributes => ({ 'data-key': attributes.key }),
      },
      tempo: {
        default: 120,
        parseHTML: element => parseInt(element.getAttribute('data-tempo'), 10) || 120,
        renderHTML: attributes => ({ 'data-tempo': attributes.tempo }),
      },
      clef: {
        default: 'treble',
        parseHTML: element => element.getAttribute('data-clef') || 'treble',
        renderHTML: attributes => ({ 'data-clef': attributes.clef }),
      },
      abcData: {
        default: '',
        parseHTML: element => element.getAttribute('data-abc') || '',
        renderHTML: attributes => ({ 'data-abc': attributes.abcData }),
      },
      musicXmlData: {
        default: '',
        parseHTML: element => element.getAttribute('data-musicxml') || '',
        renderHTML: attributes => ({ 'data-musicxml': attributes.musicXmlData }),
      },
      wrapMode: {
        default: 'wrap', // 'single' | 'wrap'
        parseHTML: element => element.getAttribute('data-wrap-mode') || 'wrap',
        renderHTML: attributes => ({ 'data-wrap-mode': attributes.wrapMode }),
      },
      notesData: {
        default: null,
        parseHTML: element => {
          const raw = element.getAttribute('data-notes');
          if (!raw) return null;
          try {
            return JSON.parse(raw);
          } catch (e) {
            return null;
          }
        },
        renderHTML: attributes => {
          if (!attributes.notesData) return {};
          return { 'data-notes': JSON.stringify(attributes.notesData) };
        },
      },
      keyChanges: {
        default: [],
        parseHTML: element => {
          const raw = element.getAttribute('data-key-changes');
          if (!raw) return [];
          try {
            const v = JSON.parse(raw);
            return Array.isArray(v) ? v : [];
          } catch (e) {
            return [];
          }
        },
        renderHTML: attributes => {
          if (!attributes.keyChanges || attributes.keyChanges.length === 0) return {};
          return { 'data-key-changes': JSON.stringify(attributes.keyChanges) };
        },
      },
      timeChanges: {
        default: [],
        parseHTML: element => {
          const raw = element.getAttribute('data-time-changes');
          if (!raw) return [];
          try {
            const v = JSON.parse(raw);
            return Array.isArray(v) ? v : [];
          } catch (e) {
            return [];
          }
        },
        renderHTML: attributes => {
          if (!attributes.timeChanges || attributes.timeChanges.length === 0) return {};
          return { 'data-time-changes': JSON.stringify(attributes.timeChanges) };
        },
      },
      tempoChanges: {
        default: [],
        parseHTML: element => {
          const raw = element.getAttribute('data-tempo-changes');
          if (!raw) return [];
          try {
            const v = JSON.parse(raw);
            return Array.isArray(v) ? v : [];
          } catch (e) {
            return [];
          }
        },
        renderHTML: attributes => {
          if (!attributes.tempoChanges || attributes.tempoChanges.length === 0) return {};
          return { 'data-tempo-changes': JSON.stringify(attributes.tempoChanges) };
        },
      },
      swing: {
        default: { on: false, amount: 0.67 },
        parseHTML: element => {
          const raw = element.getAttribute('data-swing');
          if (!raw) return { on: false, amount: 0.67 };
          try {
            const v = JSON.parse(raw);
            return { on: !!v.on, amount: Math.min(0.75, Math.max(0.5, v.amount || 0.67)) };
          } catch (e) {
            return { on: false, amount: 0.67 };
          }
        },
        renderHTML: attributes => {
          if (!attributes.swing || !attributes.swing.on) return {};
          return { 'data-swing': JSON.stringify(attributes.swing) };
        },
      },
      tempoRamps: {
        default: [],
        parseHTML: element => {
          const raw = element.getAttribute('data-tempo-ramps');
          if (!raw) return [];
          try {
            const v = JSON.parse(raw);
            return Array.isArray(v) ? v : [];
          } catch (e) {
            return [];
          }
        },
        renderHTML: attributes => {
          if (!attributes.tempoRamps || attributes.tempoRamps.length === 0) return {};
          return { 'data-tempo-ramps': JSON.stringify(attributes.tempoRamps) };
        },
      }
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="music-sheet"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-type': 'music-sheet' }),
    ];
  },

  addNodeView() {
    return VueNodeViewRenderer(MusicSheetNodeView);
  },

  addCommands() {
    return {
      insertMusicSheet: (options = {}) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: {
            title: options.title || (options.isTab ? 'Tablatura' : 'Nova Partitura'),
            timeSignature: options.timeSignature || '4/4',
            key: options.key || 'C',
            tempo: options.tempo || 120,
            clef: options.clef || 'treble',
            wrapMode: options.wrapMode || 'wrap',
            abcData: options.abcData || '',
            musicXmlData: options.musicXmlData || '',
            notesData: options.notesData || (options.isTab ? [
              {
                id: 'voice-tab-1',
                name: 'Violão / Guitarra',
                clef: 'treble',
                staffIndex: 0,
                showTab: true,
                tabOnly: !!options.tabOnly,
                notes: []
              }
            ] : null)
          }
        });
      }
    };
  }
});

export default MusicSheetExtension;
